/**
 * Fuenf kurze Fragen nach der Registrierung (Entscheidung Robert, 26.09.2026).
 *
 * WOZU: wissen, was die Leute wirklich interessiert. Die App hat kein
 * Analytics; die Antworten in `public.onboarding_antworten` sind die Messung.
 * Auswertung: `public.onboarding_auswertung` (nur service_role), siehe
 * apps/web/AGENTS.md.
 *
 * STABILE SCHLUESSEL: gespeichert wird `landingpages`, nie „Landingpages und
 * Websites". Der Wortlaut darf sich hier aendern. Wer die BEDEUTUNG einer
 * Antwort aendert oder eine entfernt, zaehlt FRAGEN_VERSION hoch, sonst zaehlt
 * die Auswertung Altes und Neues zusammen.
 *
 * 99takes ist nicht nur B2B, auch Konsumgueter und Politik (Robert,
 * 11.09.2026). Deshalb fragt Frage 2 nach der Zielgruppe.
 */

import { t } from './sprache';

/**
 * 2 seit 05.11.2026: Reihenfolge und Ablauf geaendert. Pruefen und Zielgruppe
 * kommen zuerst, dann das Verbinden mit Claude, erst danach Hakt, Rolle und
 * Quelle als freiwillige Umfrage. Die Bedeutung der Antworten ist gleich, aber
 * die drei letzten beantwortet jetzt nur, wer nach dem Verbinden noch bleibt.
 * Version 1 und 2 deshalb getrennt auswerten.
 */
export const FRAGEN_VERSION = 2;
/**
 * Die ersten KERN Fragen gehoeren zum Einstieg (Schritt 1 und 2 von 3,
 * Schritt 3 ist das Verbinden). Sie fuettern „Dein Start in Claude“
 * (erster-lauf.ts). Der Rest ist Umfrage fuer uns und kommt nach dem
 * Verbinden, wie die Umfrage im Signup-Wizard von Tappla TV.
 */
export const KERN = 2;
export const FREITEXT_MAX = 200;
export const ANDERES = 'anderes';
/**
 * „Alles davon" in einer Mehrfachfrage. Wer es anhakt, sieht alle Optionen
 * angehakt (ausser „Etwas anderes"); wer danach eine abnimmt, verliert auch
 * „Alles davon"; wer alle einzeln anhakt, bekommt es dazu (Feedback
 * Testnutzer, 28.09.2026).
 *
 * GESPEICHERT wird wie vorher nur `alles` (plus `anderes`, falls gewaehlt),
 * nicht die fuenf Einzelwerte: sonst zaehlte `onboarding_auswertung` jede
 * Einzeloption fuer jeden mit, der „Alles davon" gewaehlt hat, und Alt und
 * Neu waeren nicht mehr vergleichbar. Beim Laden wird `alles` wieder zu allen
 * Haken aufgeklappt.
 */
export const ALLES = 'alles';

export interface Frage {
  id: 'pruefen' | 'hakt' | 'rolle' | 'zielgruppe' | 'quelle';
  titel: string;
  /**
   * Der Titel in zwei Zeilen, die zweite gedaempft (seit 05.11.2026, Entwurf
   * „Einstieg nach Higgsfield“, Variante B). Zusammen ergeben sie `titel`.
   */
  kopf: [string, string];
  mehrfach: boolean;
  /**
   * Ein Satz unter den Antworten, sobald etwas gewaehlt ist (seit 29.09.2026,
   * Rundgang als Geschichte). Nur Anzeige, wird nicht gespeichert. Verspricht
   * nichts, was danach nicht kommt. Bei Frage 2 (woran hakt es) erst
   * anerkennen, nicht erklaeren (Entwurf „99takes Signup Politur",
   * 12.10.2026); „Der Satz am Ende passt dazu" verstand ohne den Satz
   * daneben niemand.
   */
  reaktion: string;
  /** Englische Fassung von Titel und Reaktion (seit 16.10.2026). Nur Anzeige. */
  en: { titel: string; kopf: [string, string]; reaktion: string };
  /** `text` deutsch, `en` englisch; gespeichert wird nur `wert`. */
  optionen: Array<{ wert: string; text: string; en: string }>;
}

/** Titel, Reaktion und Optionstext in der Sprache der App. Beim Rendern aufrufen. */
export const frageTitel = (f: Frage) => t(f.titel, f.en.titel);
export const frageKopf = (f: Frage): [string, string] => t(f.kopf, f.en.kopf);
export const frageReaktion = (f: Frage) => t(f.reaktion, f.en.reaktion);
export const optionText = (o: Frage['optionen'][number]) => t(o.text, o.en);

export const FRAGEN: Frage[] = [
  {
    id: 'pruefen', mehrfach: true, titel: 'Was willst du mit 99takes vor allem prüfen?',
    kopf: ['Was willst du mit 99takes', 'vor allem prüfen?'],
    reaktion: 'Gut. Daraus wird gleich dein erster Satz für Claude.',
    en: { titel: 'What do you mainly want to test with 99takes?', kopf: ['What do you mainly want', 'to test with 99takes?'], reaktion: 'Good. This becomes your first sentence for Claude.' },
    optionen: [
      { wert: 'landingpages', text: 'Landingpages und Websites', en: 'Landing pages and websites' },
      { wert: 'anzeigen', text: 'Anzeigen (Google, LinkedIn, Meta)', en: 'Ads (Google, LinkedIn, Meta)' },
      { wert: 'emails', text: 'E-Mails und Newsletter', en: 'Emails and newsletters' },
      { wert: 'angebote', text: 'Angebote und Preisseiten', en: 'Offers and pricing pages' },
      { wert: 'positionierung', text: 'Positionierung und Botschaften', en: 'Positioning and messaging' },
      { wert: ALLES, text: 'Alles davon', en: 'All of these' },
    ],
  },
  {
    id: 'zielgruppe', mehrfach: false, titel: 'Wen wollt ihr mit euren Texten erreichen?',
    kopf: ['Wen wollt ihr', 'mit euren Texten erreichen?'],
    reaktion: 'Danach richtet sich der Satz, mit dem du startest.',
    en: { titel: 'Who do you want to reach with your copy?', kopf: ['Who do you want to reach', 'with your copy?'], reaktion: 'This shapes the sentence you start with.' },
    optionen: [
      { wert: 'b2b', text: 'Unternehmen (B2B)', en: 'Businesses (B2B)' },
      { wert: 'b2c', text: 'Privatkunden (B2C, Konsumgüter)', en: 'Consumers (B2C, consumer goods)' },
      { wert: 'beides', text: 'Beides', en: 'Both' },
      { wert: 'politik', text: 'Wähler, Mitglieder, Öffentlichkeit', en: 'Voters, members, the public' },
      { wert: ANDERES, text: 'Etwas anderes', en: 'Something else' },
    ],
  },
  {
    id: 'hakt', mehrfach: true, titel: 'Woran hakt es gerade?',
    kopf: ['Woran hakt es', 'gerade?'],
    reaktion: 'Damit bist du nicht allein. Danke, noch zwei.',
    en: { titel: "What's getting in the way right now?", kopf: ["What's getting in the way", 'right now?'], reaktion: "You're not alone with that. Thanks, two more." },
    optionen: [
      { wert: 'konvertiert_schlecht', text: 'Seiten konvertieren zu schlecht', en: "Pages don't convert well enough" },
      { wert: 'team_uneinig', text: 'Im Team uneinig, welche Fassung besser ist', en: "The team can't agree which version is better" },
      { wert: 'zu_wenig_traffic', text: 'Zu wenig Traffic für echte A/B-Tests', en: 'Not enough traffic for real A/B tests' },
      { wert: 'tests_zu_teuer', text: 'Tests dauern zu lange oder kosten zu viel', en: 'Tests take too long or cost too much' },
      { wert: 'zielgruppe_unklar', text: 'Wir wissen nicht genau, was die Zielgruppe denkt', en: "We don't really know what the audience thinks" },
      { wert: 'ueberzeugen', text: 'Kunden oder Chefs von einer Richtung überzeugen', en: 'Convincing clients or bosses of a direction' },
      { wert: ANDERES, text: 'Etwas anderes', en: 'Something else' },
    ],
  },
  {
    id: 'rolle', mehrfach: false, titel: 'Was beschreibt deine Rolle am besten?',
    kopf: ['Was beschreibt deine Rolle', 'am besten?'],
    reaktion: 'Danke, noch eine.',
    en: { titel: 'What best describes your role?', kopf: ['What best describes', 'your role?'], reaktion: 'Thanks, one more.' },
    optionen: [
      { wert: 'gruender', text: 'Gründer oder Geschäftsführung', en: 'Founder or management' },
      { wert: 'marketing_leitung', text: 'Marketing-Leitung', en: 'Head of marketing' },
      { wert: 'performance_sea', text: 'Performance Marketing / SEA', en: 'Performance marketing / paid search' },
      { wert: 'copywriting', text: 'Copywriting / Content', en: 'Copywriting / content' },
      { wert: 'produktmarketing', text: 'Produktmarketing', en: 'Product marketing' },
      { wert: 'agentur', text: 'Agentur oder Beratung', en: 'Agency or consultancy' },
      { wert: 'politik', text: 'Politische Kommunikation', en: 'Political communication' },
      { wert: ANDERES, text: 'Etwas anderes', en: 'Something else' },
    ],
  },
  {
    id: 'quelle', mehrfach: false, titel: 'Wie bist du auf 99takes gestoßen?',
    kopf: ['Wie bist du', 'auf 99takes gestoßen?'],
    reaktion: 'Danke. Jetzt geht es zu deinem ersten Run.',
    en: { titel: 'How did you find 99takes?', kopf: ['How did you', 'find 99takes?'], reaktion: 'Thanks. On to your first run.' },
    optionen: [
      { wert: 'linkedin', text: 'LinkedIn', en: 'LinkedIn' },
      { wert: 'google', text: 'Google', en: 'Google' },
      { wert: 'ki_assistent', text: 'KI-Assistent (Claude, ChatGPT …)', en: 'AI assistant (Claude, ChatGPT …)' },
      { wert: 'empfehlung', text: 'Empfehlung', en: 'Recommendation' },
      { wert: 'blessing_digital', text: 'Blessing Digital / Robert direkt', en: 'Blessing Digital / Robert directly' },
      { wert: 'newsletter', text: 'Newsletter oder Community', en: 'Newsletter or community' },
      { wert: ANDERES, text: 'Etwas anderes', en: 'Something else' },
    ],
  },
];

export type FrageId = Frage['id'];
/** Auswahl je Frage: immer eine Liste, bei Einfachauswahl mit hoechstens einem Wert. */
export type Auswahl = Partial<Record<FrageId, string[]>>;
export type Freitexte = Partial<Record<FrageId, string>>;

/** Was in `onboarding_antworten.antworten` landet. */
export type Gespeichert = Partial<Record<FrageId, string | string[]>> & { sonstiges?: Freitexte };

// ─── Ablauf ─────────────────────────────────────────────────────────────────

export interface Stand {
  schritt: number;
  richtung: 'vor' | 'zurueck';
  auswahl: Auswahl;
  freitext: Freitexte;
}

export type Aktion =
  | { typ: 'waehlen'; frage: FrageId; wert: string }
  | { typ: 'freitext'; frage: FrageId; text: string }
  | { typ: 'weiter' }
  | { typ: 'zurueck' };

/** Die Optionen, die „Alles davon" umfasst: alle ausser ihm selbst und „Etwas anderes". */
const einzelne = (f: Frage) => f.optionen.map(o => o.wert).filter(w => w !== ALLES && w !== ANDERES);
const hatAlles = (f: Frage) => f.mehrfach && f.optionen.some(o => o.wert === ALLES);

/**
 * Macht eine Mehrfachauswahl mit „Alles davon" stimmig: `alles` genau dann,
 * wenn alle Einzeloptionen gewaehlt sind. Reihenfolge wie in FRAGEN.
 */
function stimmig(f: Frage, gewaehlt: string[]): string[] {
  if (!hatAlles(f)) return gewaehlt;
  const alle = einzelne(f).every(w => gewaehlt.includes(w));
  const menge = new Set(gewaehlt.filter(w => w !== ALLES));
  if (alle) menge.add(ALLES);
  return f.optionen.map(o => o.wert).filter(w => menge.has(w));
}

/** Umschalten einer Option in einer Mehrfachfrage, mit „Alles davon". */
function umschalten(f: Frage, bisher: string[], wert: string): string[] {
  if (wert === ALLES && hatAlles(f)) {
    const behalten = bisher.includes(ANDERES) ? [ANDERES] : [];
    return bisher.includes(ALLES)
      ? behalten
      : stimmig(f, [...einzelne(f), ...behalten]);
  }
  const neu = bisher.includes(wert) ? bisher.filter(w => w !== wert) : [...bisher, wert];
  return stimmig(f, neu);
}

export const anfang = (vorher?: Gespeichert | null): Stand => {
  const auswahl: Auswahl = {};
  const freitext: Freitexte = {};
  for (const f of FRAGEN) {
    const v = vorher?.[f.id];
    const liste = (Array.isArray(v) ? v : typeof v === 'string' ? [v] : [])
      .filter(w => f.optionen.some(o => o.wert === w));
    const aufgeklappt = hatAlles(f) && liste.includes(ALLES) ? [...liste, ...einzelne(f)] : liste;
    if (liste.length) auswahl[f.id] = f.mehrfach ? stimmig(f, [...new Set(aufgeklappt)]) : liste.slice(0, 1);
    const t = vorher?.sonstiges?.[f.id];
    if (typeof t === 'string' && t) freitext[f.id] = t.slice(0, FREITEXT_MAX);
  }
  return { schritt: 0, richtung: 'vor', auswahl, freitext };
};

export function ablauf(s: Stand, a: Aktion): Stand {
  switch (a.typ) {
    case 'waehlen': {
      const f = FRAGEN.find(x => x.id === a.frage);
      if (!f || !f.optionen.some(o => o.wert === a.wert)) return s;
      const bisher = s.auswahl[a.frage] ?? [];
      const neu = f.mehrfach
        ? umschalten(f, bisher, a.wert)
        : [a.wert];
      return { ...s, auswahl: { ...s.auswahl, [a.frage]: neu } };
    }
    case 'freitext':
      return { ...s, freitext: { ...s.freitext, [a.frage]: a.text.slice(0, FREITEXT_MAX) } };
    case 'weiter':
      return { ...s, schritt: Math.min(s.schritt + 1, FRAGEN.length), richtung: 'vor' };
    case 'zurueck':
      return { ...s, schritt: Math.max(s.schritt - 1, 0), richtung: 'zurueck' };
  }
}

/** Auf eine bestimmte Frage springen (vom Verbinden zurueck, oder in die Umfrage). Antworten bleiben stehen. */
export const springe = (s: Stand, schritt: number, richtung: Stand['richtung']): Stand =>
  ({ ...s, schritt: Math.max(0, Math.min(schritt, FRAGEN.length)), richtung });

/** Letzte Frage beantwortet, der Ablauf ist durch. */
export const fertig = (s: Stand) => s.schritt >= FRAGEN.length;

/**
 * Aus dem Stand wird, was gespeichert wird. Freitext nur, wenn „Etwas anderes"
 * gewaehlt ist, sonst bliebe ein Text stehen, den die Auswahl nicht mehr
 * traegt. Unbeantwortete Fragen fehlen, statt leer dazustehen: so zaehlt n in
 * der Auswertung nur, wer die Frage wirklich beantwortet hat.
 */
export function zumSpeichern(s: Stand): Gespeichert {
  const aus: Gespeichert = {};
  const sonstiges: Freitexte = {};
  for (const f of FRAGEN) {
    const w = s.auswahl[f.id] ?? [];
    if (!w.length) continue;
    // Mit „Alles davon" nur der Schluessel `alles`, nicht die Einzelwerte (siehe ALLES).
    aus[f.id] = !f.mehrfach ? w[0] : w.includes(ALLES) ? w.filter(x => x === ALLES || x === ANDERES) : [...w];
    const t = (s.freitext[f.id] ?? '').trim();
    if (w.includes(ANDERES) && t) sonstiges[f.id] = t.slice(0, FREITEXT_MAX);
  }
  if (Object.keys(sonstiges).length) aus.sonstiges = sonstiges;
  return aus;
}

// ─── Datenzugriff ───────────────────────────────────────────────────────────

/**
 * offen: keine Zeile, die Fragen erscheinen.
 * erledigt: beantwortet oder uebersprungen, nicht noch einmal aufdraengen.
 * aus: die Tabelle fehlt (Migration noch nicht angewendet) oder die Abfrage
 *      scheitert. Dann laeuft die App ohne Fragen weiter, statt jemanden an
 *      einer Frage festzuhalten, die sich nicht speichern laesst.
 */
export type Zustand = { zustand: 'offen' } | { zustand: 'erledigt'; antworten: Gespeichert } | { zustand: 'aus' };

/** Der Teil des Supabase-Clients, den wir brauchen. Klein gehalten, damit Tests ihn nachbauen koennen. */
export interface Db {
  from(tabelle: string): any;
}

const TABELLE = 'onboarding_antworten';

export async function ladeOnboarding(db: Db): Promise<Zustand> {
  try {
    const { data, error } = await db.from(TABELLE).select('antworten, abgeschlossen_at, uebersprungen_at').maybeSingle();
    if (error) {
      console.warn('[onboarding] Fragen aus:', error.message ?? error);
      return { zustand: 'aus' };
    }
    if (!data) return { zustand: 'offen' };
    return { zustand: 'erledigt', antworten: (data.antworten ?? {}) as Gespeichert };
  } catch (e) {
    console.warn('[onboarding] Fragen aus:', e);
    return { zustand: 'aus' };
  }
}

export async function speichereOnboarding(
  db: Db, kontoId: string, antworten: Gespeichert, art: 'fertig' | 'uebersprungen',
): Promise<boolean> {
  const jetzt = new Date().toISOString();
  try {
    const { error } = await db.from(TABELLE).upsert({
      account_id: kontoId,
      antworten,
      fragen_version: FRAGEN_VERSION,
      ...(art === 'fertig' ? { abgeschlossen_at: jetzt } : { uebersprungen_at: jetzt }),
    }, { onConflict: 'account_id' });
    if (error) {
      console.warn('[onboarding] nicht gespeichert:', error.message ?? error);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[onboarding] nicht gespeichert:', e);
    return false;
  }
}
